import { z } from 'zod'
import { heatmapStrong } from './tailwind'

/** Shared Universal Token Rating config types and presentation helpers. */

export type UtrCriterionAxis = 'disclosure' | 'performance'

export interface UtrCriterionDefinition {
  key: string
  label: string
  axis: UtrCriterionAxis
  category: string
  weight: number
}

// UTR-specific sector taxonomy, distinct from the platform's sector_type enum.
export const UtrSectorValues = [
  'Lending, RWA, & DeFi Spot',
  'Perpetuals DEX',
  'DeSci',
  'Launchpad',
  'AI',
  'Infrastructure',
  'Privacy',
] as const
export type UtrSectorType = (typeof UtrSectorValues)[number]

const UTR_HEATMAP_STOPS = [
  heatmapStrong[0],
  heatmapStrong[10],
  heatmapStrong[20],
  heatmapStrong[30],
  heatmapStrong[40],
  heatmapStrong[50],
  heatmapStrong[60],
  heatmapStrong[70],
  heatmapStrong[80],
  heatmapStrong[90],
  heatmapStrong[100],
] as const

export function utrHeatmapColor(pct: number): string {
  const value = Math.min(Math.max(pct, 0), 100)
  return UTR_HEATMAP_STOPS[Math.floor(value / 10)] ?? UTR_HEATMAP_STOPS[0]
}

export const UtrApplicabilityValues = ['preTge', 'postTge', 'both'] as const
export type UtrApplicability = (typeof UtrApplicabilityValues)[number]

export const UtrGradingModeValues = ['auto', 'manual'] as const
/** Auto rubrics drive scoring; manual rubrics provide analyst guidance. */
export type UtrGradingMode = (typeof UtrGradingModeValues)[number]

export interface UtrConfigCriterion extends UtrCriterionDefinition {
  definition: string
  applicability: UtrApplicability
  grading: UtrGradingMode
  /** Ordered from score 10 to 0. */
  rubric: string[]
  bands: UtrConfigBand[]
}

export interface UtrConfigBand {
  score: number
  explanation: string | null
}

export interface UtrExchangeRef {
  id: string
  /** Canonical CoinGecko display name. */
  name: string
  logoUrl: string | null
}

export interface UtrExchangeTiersConfig {
  tier1: UtrExchangeRef[]
  tier2: UtrExchangeRef[]
  /** Tier 3 contains all unlisted venues. */
  excluded: UtrExchangeRef[]
}

export interface UtrGradingScaleRow {
  min: number | null
  grade: string
  interpretation: string
}

export interface UtrGradingAxis {
  description: string
  scale: UtrGradingScaleRow[]
}

export interface UtrGradingConfig {
  disclosure: UtrGradingAxis
  performance: UtrGradingAxis
  overall: UtrGradingAxis
}

export interface UtrConfig {
  criteria: UtrConfigCriterion[]
  exchangeTiers: UtrExchangeTiersConfig
  grading?: UtrGradingConfig
}

const utrExchangeRefSchema = z.object({
  id: z.string().min(1).max(100),
  name: z.string().min(1).max(100),
  logoUrl: z.string().nullable(),
})

const utrConfigCriterionSchema = z.object({
  key: z.string().min(1).max(80),
  label: z.string().min(1).max(200),
  axis: z.enum(['disclosure', 'performance']),
  category: z.string().min(1).max(120),
  weight: z.number().min(0).max(1),
  definition: z.string().max(2000),
  applicability: z.enum(UtrApplicabilityValues),
  grading: z.enum(UtrGradingModeValues),
  rubric: z.array(z.string().max(1000)),
  bands: z.array(
    z.object({
      score: z.number().int().min(0).max(10),
      explanation: z.string().max(1000).nullable(),
    }),
  ),
})

const utrGradingAxisSchema = z.object({
  description: z.string(),
  scale: z.array(
    z.object({
      min: z.number().nullable().default(null),
      grade: z.string(),
      interpretation: z.string(),
    }),
  ),
})

/** Validates config shape; save-time rules are enforced separately. */
export const utrConfigSchema = z.object({
  criteria: z
    .array(utrConfigCriterionSchema)
    .min(1)
    .superRefine((criteria, ctx) => {
      const seen = new Set<string>()
      for (const criterion of criteria) {
        if (seen.has(criterion.key)) {
          ctx.addIssue({ code: z.ZodIssueCode.custom, message: `Duplicate criterion key: ${criterion.key}` })
        }
        seen.add(criterion.key)
      }
    }),
  exchangeTiers: z.object({
    tier1: z.array(utrExchangeRefSchema),
    tier2: z.array(utrExchangeRefSchema),
    excluded: z.array(utrExchangeRefSchema),
  }),
  grading: z
    .object({
      disclosure: utrGradingAxisSchema,
      performance: utrGradingAxisSchema,
      overall: utrGradingAxisSchema,
    })
    .optional(),
})

export type UtrConfigInput = z.input<typeof utrConfigSchema>

export function utrWeightedAxisScore(
  criteria: Pick<UtrCriterionDefinition, 'key' | 'weight'>[],
  scores: Record<string, number>,
): number {
  const total = criteria.reduce((sum, criterion) => sum + criterion.weight * (scores[criterion.key] ?? 0), 0)
  return Math.round(total * 100) / 100
}

/**
 * Temporary verified cohort, until UTR project ownership lands in the API —
 * `UtrAssetResponse.verified` is still pinned to `false`.
 *
 * Shared rather than app-local because the share card is rendered server-side:
 * a client-only list would put the "LISTED TOKEN" seal on the page but leave it
 * off the shared image.
 */
const VERIFIED_UTR_ARCTIC_IDS = new Set([
  'bae53426-1101-4f17-a360-95f88fd2be7d', // Zama
  '5fc03eaf-d0e7-4bc2-96c8-8381067408d4', // Solana Mobile
  '456f2d94-0e2d-4d6e-aa4f-f1f652982cc8', // Metaplex
  '8fff4e19-32e8-4bad-ba24-0f2f8769254f', // Boundless
  '704c2aed-6429-48ce-8a17-6a52fd03232b', // Mezo
  'b824db3b-2ffc-49d9-8a28-c49080ddde5c', // Espresso Systems
  'd73bf52f-cf41-45da-8d55-6843b6dc5403', // Orca
  'e609895e-66f3-4ea2-8cd5-79260ca03b03', // Raydium
  '7b705b40-f219-4d5d-9d47-517475613fcd', // Pacifica
  '49cf10e0-5c95-476d-a7d0-4bbbbecb2c9d', // RedStone
  'e72348c4-98ce-4e35-b833-bc0fb490fe94', // SKALE
  'a3d95cc8-1616-4d0e-bb40-d2f3382ebd22', // Meteora
  '2325920d-122c-4e83-bd5c-1ca05035a04c', // Chainlink
  '310c53c6-0ec7-4590-b39a-d881f9320255', // Aethir
  'cc113318-d850-4c50-8d72-411e35443781', // Turtle
  '687c72fe-a558-4b68-8ea3-4c40d5bc6e8c', // Curve DAO
  '76e14d92-cbd4-493b-ba42-1931f5d75a54', // Pendle
  'e63c0308-835c-4aac-88c2-4b27b97cf3bb', // o1.exchange
  '9599ecb0-d46a-4c77-a2e2-04e98a1e18d1', // Lighter
  '715f370d-5bb1-4236-a0c1-05b4e8899a3e', // Ether.fi
  'd5ff0d76-e7b9-4f94-9775-73c3b4ced82c', // BOB (Build on Bitcoin)
  '4162bf75-891e-41da-ac37-6286c8870d69', // Arcium
  'c5c3f395-a44e-4244-87da-48593de4b822', // Mantle
  'f6e97827-619b-4edb-8186-00932324d70b', // Virtuals Protocol
  '51bb2dd5-fc82-487e-9387-e4a39a74e90c', // Variational
  'b7141257-3d89-4152-99aa-2dad96926600', // Kinetiq
  'cd87993e-5032-468c-b52e-22ed38cde81a', // Aerodrome Finance
  'e60b0f82-af17-495d-9b32-4bcfc95141b2', // Saffron
])

export function isUtrVerified(arcticId: string, apiVerified: boolean): boolean {
  return apiVerified || VERIFIED_UTR_ARCTIC_IDS.has(arcticId)
}
